// Atlas R6 (findings #17/#18) — batch observation signals (reliability + last-seen)
// for channel rows + Live search ranking. Module-level reactive cache so multiple
// consumers (the Live sidebar, search ranking) share one fetch and one set of
// data. Cold-start returns verdict 'none' ("Not tried"); consumers render a pill
// ONLY for real history (good/mixed/bad), so day-one rows stay clean rather than
// showing "Not tried" on every row (the original honest deferral, now wired).

import { reactive } from 'vue';
import { api, type ObservationSignal } from '@/lib/api';
import { log } from '@/lib/log';

const cache = reactive(new Map<string, ObservationSignal>());

function keyOf(sourceId: number, itemId: string): string {
    return `${sourceId}:${itemId}`;
}

/**
 * Drop + re-fetch the cached signal for channels whose observations just
 * changed (post-4.10 rule #7 — a mutation ships its downstream-refetch).
 * Called by useObservationCapture after a successful flush: without it, the
 * cache-guard in `fetchFor` pinned the PRE-tune signal (often "none") for the
 * whole session, so the reliability dot / quality line only appeared after a
 * page refresh. Module-level (not inside the composable) so the capture
 * module can import it without instantiating the composable.
 */
export async function refreshObservationSignals(
    channels: Array<{ sourceId: number; id: string }>,
): Promise<void> {
    for (const c of channels) cache.delete(keyOf(c.sourceId, c.id));
    const want = channels.slice(0, 200).map((c) => ({ sourceId: c.sourceId, itemId: c.id }));
    if (want.length === 0) return;
    try {
        const { signals } = await api.observations.signals(want);
        for (const [k, v] of Object.entries(signals)) cache.set(k, v);
    } catch (err) {
        log.warn('observationSignals.refresh_failed', { error: (err as Error).message });
    }
}

/**
 * Module-level read of a channel's cached signal. The cache is reactive, so
 * reading this inside a computed (e.g. channelList's measured-quality filter)
 * re-runs the computed when probes/observations land.
 */
export function getObservationSignal(sourceId: number, itemId: string): ObservationSignal | undefined {
    return cache.get(keyOf(sourceId, itemId));
}

/**
 * Merge LIVE player-measured quality into the cached signal for the channel
 * that is currently PLAYING. Pure client-side cache write — no server
 * round-trip and no extra observation row (the durable row still lands when
 * the tune-in finalizes on switch-away). Without this, the playing channel's
 * row only showed its measured quality after the user left it. Reliability is
 * preserved (or stays "none" — the metrics chip renders independently of the
 * verdict, so a first-ever tune shows numbers without fabricating a verdict).
 */
export function noteProvisionalQuality(
    sourceId: number,
    itemId: string,
    q: {
        height?: number | null;
        bitrate?: number | null;
        fps?: number | null;
        videoCodec?: string | null;
        audioCodec?: string | null;
        audioChannels?: number | null;
        audioTracks?: number | null;
        subtitleTracks?: number | null;
    },
): void {
    if (!Number.isFinite(sourceId) || !itemId) return;
    // Deliberately NOT widened for the track counts: they always ride
    // alongside measured quality from the one call site, and admitting a
    // counts-only update would let the PREVIOUS channel's counts land under
    // the newly-tuned channel's key (pre-commit review 2026-08-25 #4).
    if (q.height == null && q.bitrate == null && q.fps == null) return;
    const k = keyOf(sourceId, itemId);
    const existing = cache.get(k);
    cache.set(k, {
        reliability: existing?.reliability ?? { verdict: 'none', rate: null, label: 'Not tried' },
        lastSeen: {
            height: q.height ?? existing?.lastSeen?.height ?? null,
            bitrate: q.bitrate ?? existing?.lastSeen?.bitrate ?? null,
            fps: q.fps ?? existing?.lastSeen?.fps ?? null,
            videoCodec: q.videoCodec ?? existing?.lastSeen?.videoCodec ?? null,
            audioCodec: q.audioCodec ?? existing?.lastSeen?.audioCodec ?? null,
            audioChannels: q.audioChannels ?? existing?.lastSeen?.audioChannels ?? null,
            // Track counts describe the CHANNEL (how many languages it
            // carries), so a live tune-in now supplies them too — before
            // Phase 4 only the probe path did, and the live path measured
            // the single-track local buffer, so the row's N×audio chip
            // never lit. Carried over when this update doesn't have them.
            audioTracks: q.audioTracks ?? existing?.lastSeen?.audioTracks ?? null,
            subtitleTracks: q.subtitleTracks ?? existing?.lastSeen?.subtitleTracks ?? null,
            ageMs: 0,
            varies: existing?.lastSeen?.varies ?? false,
        },
    });
}

export function useObservationSignals() {
    /** Fetch signals for channels not already cached (capped at 200/call). */
    async function fetchFor(channels: Array<{ sourceId: number; id: string }>): Promise<void> {
        const want = channels
            .filter((c) => !cache.has(keyOf(c.sourceId, c.id)))
            .slice(0, 200)
            .map((c) => ({ sourceId: c.sourceId, itemId: c.id }));
        if (want.length === 0) return;
        try {
            const { signals } = await api.observations.signals(want);
            for (const [k, v] of Object.entries(signals)) cache.set(k, v);
        } catch (err) {
            log.warn('observationSignals.fetch_failed', { error: (err as Error).message });
        }
    }

    /** The reliability verdict for a channel, or null when never tried (no pill). */
    function reliabilityVerdict(sourceId: number, itemId: string): 'good' | 'mixed' | 'bad' | null {
        const s = cache.get(keyOf(sourceId, itemId));
        if (!s || s.reliability.verdict === 'none') return null;
        return s.reliability.verdict;
    }

    function get(sourceId: number, itemId: string): ObservationSignal | undefined {
        return cache.get(keyOf(sourceId, itemId));
    }

    return { fetchFor, reliabilityVerdict, get };
}

/** Test hook — drop the module-level cache. */
export function _resetObservationSignalsForTests(): void {
    cache.clear();
}
